import { Menu } from '@mui/material';
import React, { FC } from 'react';
import styled from 'styled-components';
import {
  FitClientBasicsFragment,
  useMemberClientQuery,
} from '../../graphql/types';
import { SplitFlags } from '../../hooks/featureFlags';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import useTracking from '../../hooks/useTracking';
import { GRAY200, HOVER } from '../../theme';
import {
  EventCategories,
  EventNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { InlineShowMore } from '../reusable/ShowMore/Inline';
import Text, { TextKind } from '../reusable/Text';

export interface TemplateDefinition {
  title: string;
  message: string;
}

export enum TemplateOption {
  MEMBER_FIRST_NAME = 'MEMBER_FIRST_NAME',
}

type TemplateOptions = Record<TemplateOption, string | undefined | null>;

export const getChatTemplates = (
  templateOptions: TemplateOptions,
): TemplateDefinition[] => [
  {
    title: 'Schedule First Session',
    message:
      'Looking for a personalized plan to achieve your goals? 💪 Click below to schedule a 30-minute complimentary Coaching Session 📅  https://fit.teamexos.com/coaching',
  },
  {
    title: 'Reminder Upcoming Coaching Session',
    message: `We've got a Coaching Session coming soon!  I'm excited to chat about your goals 💪 

If you have questions or need to reschedule, feel free to reach out. 
  
Looking forward to it! 😀`,
  },
  {
    title: 'Coaching Session Follow Up',
    message:
      'Hi! I wanted to follow-up with you... How are things going since our session?',
  },
  {
    title: 'No Show',
    message:
      "Hey there! I missed you today for our Coaching Session. Let's try to reschedule. Click below to book another session 📅 Hope to see you soon! 😀 https://fit.teamexos.com/coaching",
  },
  {
    title: 'QA Recording Approval',
    message: `${
      templateOptions[TemplateOption.MEMBER_FIRST_NAME] ?? 'Hello'
    }, I want to ask permission prior to our 1 on 1 coaching session if I can record our meeting as part of the Exos Quality Assurance Process. Even the coaches at Exos get coached! Part of that process entails reviewing the execution of my skills with my manager. We will delete the recording within 30 days after the review process. My manager would be the only other person seeing this. Please let me know if you are or are not ok with recording the session. We will meet for our 1 on 1 session either way and I can always find another member to record for this quality assurance process.`,
  },
];

const getPtChatTemplates = (clientId: string): TemplateDefinition[] => {
  switch (clientId) {
    case 'f485e965-d3f5-47be-8a0b-7c48a1070ac3': // J&J
      return [
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: https://buy.stripe.com/28o6st7Wyekc3wA5kn`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: https://buy.stripe.com/4gw7wxdgSgsk6IM3ce`,
        },
        {
          title: 'Personal Training - Stripe - 10 Sessions',
          message: `Use this link to purchase a package of 10 Personal Training Sessions: https://buy.stripe.com/fZeeYZ1ya5NG3wA289`,
        },
      ];
    case '0e3bbdd8-b1ee-4860-bd57-ac73f53f80ce': // AmeriHealth
      return [
        {
          title: 'Personal Training - Intro - 3 Sessions',
          message: `Use this link to purchase the Introductory Package of 3 Personal Training Sessions (for $99): https://buy.stripe.com/8wM2aedRu8CEbmM5kn`,
        },
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: https://buy.stripe.com/bIYg146p25qs0I88wy`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: https://buy.stripe.com/dR6aGKeVy0680I89AB`,
        },
        {
          title: 'Personal Training - Stripe - 10 Sessions',
          message: `Use this link to purchase a package of 10 Personal Training Sessions: https://buy.stripe.com/8wMeX0aFi1aceyYcMM`,
        },
      ];
    case 'c28d8b5b-14c8-481d-9277-c58ceae4a25e': // Pfizer
      return [
        {
          title: 'Personal Training - Intro - 3 Sessions',
          message: `Use this link to purchase the Introductory Package of 3 Personal Training Sessions (for $123): https://buy.stripe.com/8x2eVeddF0pegEl1EGg3600`,
        },
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: https://buy.stripe.com/fZufZigpR2xm9bTbfgg3603`,
        },
        {
          title: 'Personal Training - Stripe - 3 Sessions',
          message: `Use this link to purchase a package of 3 Personal Training Sessions: https://buy.stripe.com/cNiaEY7Tl7RG87Persg3602`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: https://buy.stripe.com/14A5kE6Pheg473L4QSg3601`,
        },
      ];
    case 'd3be19bb-f000-4a88-8347-ea326d088b2f': // Exos Dev Environment
      return [
        {
          title: 'Personal Training - Intro - 3 Sessions',
          message: `Use this link to purchase the Introductory Package of 3 Personal Training Sessions: sample-intro-link`,
        },
        {
          title: 'Personal Training - Stripe - 1 Session',
          message: `Use this link to purchase a single Personal Training Session: sample-single-link`,
        },
        {
          title: 'Personal Training - Stripe - 5 Sessions',
          message: `Use this link to purchase a package of 5 Personal Training Sessions: sample-five-link`,
        },
        {
          title: 'Personal Training - Stripe - 10 Sessions',
          message: `Use this link to purchase a package of 10 Personal Training Sessions: sample-ten-link`,
        },
      ];
    default:
      return [];
  }
};

const getTrainingPlanTemplates = (): TemplateDefinition[] => [
  {
    title: 'Training Plans Response',
    message: `I'd love to build a personalized Training Plan for you! To make sure it fits your goals, schedule, and equipment, could you send me these four details?

Level: (Beginner or Intermediate)

Session Length: (15, 30, 45, or 60 minutes)

Workouts per Week: (2, 3, or 4 days/week)

Equipment Access: (No Equipment, Dumbbells Only, or Full Gym)

Once I have your answers, I'll get started on your plan and get it over to you!`,
  },
];

const TemplateItem = styled.li`
  border-bottom: 1px solid var(--main-border, ${GRAY200});
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  white-space: pre-line;
  &:hover {
    background-color: var(--main-hover, ${HOVER});
  }
  &:last-child {
    border-bottom: none;
  }
`;

export const TemplateMenu: FC<{
  open: boolean;
  anchorEl: HTMLElement | null;
  chatId?: string | null;
  memberId?: string | null;
  fitClient?: FitClientBasicsFragment | null;
  onTemplateClick: (template: TemplateDefinition, index: number) => void;
  onBlur: () => void;
  templateOptions: TemplateOptions;
}> = ({
  open,
  anchorEl,
  onTemplateClick,
  onBlur,
  chatId,
  memberId,
  fitClient: initialFitClient = null,
  templateOptions,
}) => {
  const { track } = useTracking();
  const { flags } = useFeatureFlags();

  // some members queries might not include the fitClient, so we need to fetch it separately if it's not included
  const { data: clientData } = useMemberClientQuery({
    skip: !!initialFitClient || !memberId,
    variables: {
      id: memberId ?? '',
    },
  });
  const fetchedFitClient = clientData?.user?.fitClient;

  const fitClient = initialFitClient || fetchedFitClient;

  const handleBlur = (event: React.FocusEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      onBlur();
    }
  };

  const handleTemplateClick = (template: TemplateDefinition, index: number) => {
    onTemplateClick(template, index);
    track(EventNames.TemplateSelect, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Template,
      chat_id: chatId,
      event_label: template.title,
      position: index,
    });
  };

  const handleSeeMoreClick = (
    isExpanded: boolean,
    template: TemplateDefinition,
    index: number,
  ) => {
    // If it is currently expanded, it means they clicked on See More, otherwise they clicked on See Less
    const eventName = isExpanded
      ? EventNames.SeeMoreClick
      : EventNames.SeeLessClick;
    track(eventName, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Template,
      chat_id: chatId,
      event_label: template.title,
      position: index,
    });
  };

  const templatesToDisplay = getChatTemplates(templateOptions);

  // if user has access to training plans, add those templates
  const trainingPlansEnabled = flags[SplitFlags.CH_TRAINING_PLANS];
  if (trainingPlansEnabled) {
    const trainingPlanTemplates = getTrainingPlanTemplates();
    templatesToDisplay.push(...trainingPlanTemplates);
  }

  // const clientPtLinks = (fitClient?.id && clientLinks[fitClient.id]) || null;
  const ptChatTemplates = getPtChatTemplates(fitClient?.id ?? '');
  if (fitClient?.usesPersonalTraining && ptChatTemplates.length > 0) {
    templatesToDisplay.push(...ptChatTemplates);
  }

  return (
    <Menu
      style={{ marginTop: -16 }}
      disableScrollLock
      anchorEl={anchorEl}
      open={open}
      onBlur={handleBlur}
      MenuListProps={{
        disablePadding: true, // Disable default MUI Menu padding of 8px top and bottom
      }}
      slotProps={{
        paper: {
          style: {
            maxHeight: 94 * 3.4, // This makes the menu scrollable. 94 is the height of each template item, 3.4 shows 3 items plus a little extra to see the next item
            width: 302,
          },
        },
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 8 }}
    >
      {templatesToDisplay.map((template, index) => (
        <TemplateItem
          data-testid={`template-${index}`}
          key={template.title}
          onClick={() => handleTemplateClick(template, index)}
        >
          <Text kind={TextKind.BodySM}>{template.title}</Text>
          <InlineShowMore.BodyS
            truncationWidth={250}
            lines={2}
            onToggleClick={(isExpanded) =>
              handleSeeMoreClick(isExpanded, template, index)
            }
          >
            {template.message}
          </InlineShowMore.BodyS>
        </TemplateItem>
      ))}
    </Menu>
  );
};
